import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Avatar,
    Chip,
    useTheme,
    Fade,
    Slide,
    Zoom,
    CircularProgress,
    LinearProgress,
    Button,
    Select,
    MenuItem
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    Circle
} from '@mui/icons-material';
import { useBAClientSummaryMutation, useBAOverViewSummaryMutation, useBAPerformanceScoreMutation, useBaReferralListMutation, useViewBaPerformanceMutation } from '../../redux/Recruiters/Recruiters.api';
import { useLocation } from 'react-router-dom';
import { SnackbarContext } from '../../reusable_components/components/Snackbar';
import {
    Search,
    UserCheck,
    Star,
    Clock,
    Briefcase,
    Users,
    Activity,
    Lightbulb
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend
} from 'recharts';
import GaugeComponent from "react-gauge-component";
import './RecruiterDetails.css';

// ========== ALL STYLED COMPONENTS OUTSIDE ==========
const StickyHeader = styled(Box)(({ theme, issticky }) => ({
    position: issticky === 'true' ? 'sticky' : 'static',
    top: 0,
    backgroundColor: theme.palette.background.default,
    zIndex: 1000,
    padding: '10px',
    borderBottom: issticky === 'true' ? `1px solid ${theme.palette.divider}` : 'none',
    boxShadow: issticky === 'true' ? theme.shadows[1] : 'none',
    transition: 'all 0.3s ease-in-out',
}));

const MainContent = styled(Box)(({ theme }) => ({
    overflowY: 'auto',
    height: 'calc(100vh - 120px)',
    '&::-webkit-scrollbar': {
        width: '6px'
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#c1c1c1',
        borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: '#a8a8a8'
    }
}));

const MetricCard = styled(Card)(({ theme }) => ({
    borderRadius: '8px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    height: '100%',
    transition: 'transform 0.3s ease, opacity 0.5s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
    }
}));

const PerformanceContainer = styled(Paper)(({ theme }) => ({
    borderRadius: '8px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    padding: theme.spacing(2),
    height: '450px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
}));

const ScrollableSection = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
        width: '4px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '2px',
    },
}));

const StatusIndicator = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: '#4CAF50',
    fontSize: '12px',
    fontWeight: 500
}));

// ========== SUB COMPONENTS (OUTSIDE) ==========
const StarRating = React.memo(({ rating }) => {
    const [animatedRating, setAnimatedRating] = useState(0);
    const animationRef = useRef({
        hasCompleted: false,
        frameId: null
    });

    const numericRating = parseFloat(rating) || 0;
    const maxRating = 5;

    useEffect(() => {
        const animation = animationRef.current;

        if (animation.frameId) {
            cancelAnimationFrame(animation.frameId);
            animation.frameId = null;
        }

        if (animation.hasCompleted) {
            setAnimatedRating(numericRating);
            return;
        }

        const duration = 1500;
        const startTime = Date.now();
        setAnimatedRating(0);

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOutProgress = 1 - Math.pow(1 - progress, 3);

            setAnimatedRating(numericRating * easeOutProgress);

            if (progress < 1) {
                animation.frameId = requestAnimationFrame(animate);
            } else {
                animation.hasCompleted = true;
                animation.frameId = null;
            }
        };

        animation.frameId = requestAnimationFrame(animate);

        return () => {
            if (animation.frameId) {
                cancelAnimationFrame(animation.frameId);
                animation.frameId = null;
            }
        };
    }, [numericRating]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {[...Array(maxRating)].map((_, index) => {
                const starValue = index + 1;
                let fillPercentage = 0;

                if (animatedRating >= starValue) {
                    fillPercentage = 100;
                } else if (animatedRating > starValue - 1) {
                    fillPercentage = (animatedRating - (starValue - 1)) * 100;
                }

                return (
                    <Box key={index} sx={{ position: 'relative', width: 24, height: 24 }}>
                        <Box sx={{ position: 'absolute', width: '100%', height: '100%', color: '#e0e0e0' }}>
                            <Star sx={{ fontSize: 24 }} />
                        </Box>
                        <Box sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            color: 'primary.main',

                            clipPath: `inset(0 ${100 - fillPercentage}% 0 0)`,
                            overflow: 'hidden',
                        }}>
                            <Star sx={{ fontSize: 24, backgroundColor: 'primary.main', }} />
                        </Box>
                    </Box>
                );
            })}
            <Typography variant="h5" sx={{ ml: 1.5, fontWeight: 700, color: '#333', minWidth: '50px' }}>
                {animatedRating.toFixed(1)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', alignSelf: 'flex-end', mb: 0.5 }}>
                /5
            </Typography>
        </Box>
    );
});

const ActivityCard = React.memo(({ activity }) => {
    const IconComponent = activity.icon || UserCheck;

    return (
        <Fade in timeout={300}>
            <Card sx={{
                width: '100%',
                minHeight: '80px',
                transition: 'all 0.2s ease',
                border: '1px solid #e0e0e0',
                boxShadow: 'none',
                '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderColor: activity.color
                },
                mb: 1
            }}>
                <CardContent sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': { pb: 2 }
                }}>
                    <Box sx={{
                        width: '4px',
                        height: '40px',
                        backgroundColor: activity.color || '#2196F3',
                        borderRadius: '2px',
                        flexShrink: 0
                    }} />
                    <Box sx={{
                        backgroundColor: `${activity.color || '#2196F3'}15`,
                        color: activity.color || '#2196F3',
                        borderRadius: '6px',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        {IconComponent && <IconComponent size={16} />}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}>
                            {activity?.title || activity?.referralItemName || 'Untitled'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                            {activity?.company || ''}
                        </Typography>
                        {activity?.referralItemStatus && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2, fontSize: '10px' }}>
                                Status: {activity.referralItemStatus}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Clock size={12} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                {activity?.time || 'Recently'}
                            </Typography>
                        </Box>
                        <Chip label={activity?.timeline || activity?.referralItemType} size="small" sx={{
                            backgroundColor: `${activity.color || '#2196F3'}15`,
                            color: activity.color || '#2196F3',
                            fontWeight: 500,
                            fontSize: '10px',
                            height: '20px'
                        }} />
                    </Box>
                </CardContent>
            </Card>
        </Fade>
    );
});

const AIOverview = React.memo(({ aiResponse, clientSummary, loading = false }) => {
    const numericRating = useMemo(() => {
        return parseFloat(aiResponse?.data?.performanceRating?.split('/')[0]) || 0;
    }, [aiResponse?.data?.performanceRating]);

    if (loading) {
        return (
            <PerformanceContainer>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        Analyzing performance data...
                    </Typography>
                </Box>
            </PerformanceContainer>
        );
    }

    if (!aiResponse) {
        return (
            <PerformanceContainer>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Activity size={48} color="#ccc" />
                    <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                        No performance data available
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        AI overview will appear here once generated
                    </Typography>
                </Box>
            </PerformanceContainer>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'green' }}>
                    AI Performance Overview
                </Typography>
            </Box>
            <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f0f0f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUp size={20} color="#8C69DE" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                        Performance Rating
                    </Typography>
                </Box>
                <StarRating rating={aiResponse?.data.performanceRating || '0.0'} />
            </Box>
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Activity size={20} color="#9C27B0" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Performance Summary
                    </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {aiResponse?.data?.performanceSummary || "No summary available"}
                    </Typography>
                </Paper>
            </Box>
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Lightbulb size={20} color="#FF9800" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Improvement Suggestions
                    </Typography>
                </Box>

                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fff', borderRadius: '6px', borderColor: '#e0e0e0' }}>
                    {clientSummary?.data && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                                    Client Performance
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                    {clientSummary?.data?.ClientRating}/10
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={(parseInt(clientSummary?.data?.ClientRating) / 10) * 100}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#f0f0f0',
                                    mb: 1,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getScoreColor(clientSummary?.data?.ClientRating),
                                        borderRadius: 4,
                                    }
                                }}
                            />
                            {clientSummary?.data?.ClientReferralSummary && (
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#555' }}>
                                    {clientSummary?.data?.ClientReferralSummary}
                                </Typography>
                            )}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
                               Improvement Suggestions :
                            </Typography>
                            {clientSummary?.data?.Suggestions && (
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#555' }}>
                                    💡 {clientSummary?.data?.Suggestions}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Position/Job Score & Suggestions */}
                    {aiResponse?.data?.positionScore && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                                    Job Position Performance
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                    {aiResponse?.data?.positionScore}/10
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={(parseInt(aiResponse?.data?.positionScore) / 10) * 100}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#f0f0f0',
                                    mb: 1,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getScoreColor(aiResponse?.data?.positionScore),
                                        borderRadius: 4,
                                    }
                                }}
                            />
                            {aiResponse?.data?.positionSuggestions && (
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#555' }}>
                                    💡 {aiResponse?.data?.positionSuggestions}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Candidate Score & Suggestions */}
                    {aiResponse?.data?.candidateScore && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                                    Candidate Performance
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                    {aiResponse?.data?.candidateScore}/10
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={(parseInt(aiResponse?.data?.candidateScore) / 10) * 100}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#f0f0f0',
                                    mb: 1,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getScoreColor(aiResponse?.data?.candidateScore),
                                        borderRadius: 4,
                                    }
                                }}
                            />
                            {aiResponse?.data?.candidateSuggestions && (
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#555' }}>
                                    💡 {aiResponse?.data?.candidateSuggestions}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Joined Score & Suggestions */}
                    {aiResponse?.data?.joinedScore && (
                        <Box sx={{ mb: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                                    Joined Performance
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                    {aiResponse?.data?.joinedScore}/10
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={(parseInt(aiResponse?.data?.joinedScore) / 10) * 100}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#f0f0f0',
                                    mb: 1,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getScoreColor(aiResponse?.data?.joinedScore),
                                        borderRadius: 4,
                                    }
                                }}
                            />
                            {aiResponse?.data?.joinedSuggestions && (
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#555' }}>
                                    💡 {aiResponse?.data?.joinedSuggestions}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* {!aiResponse?.data?.clientScore && !aiResponse?.data?.positionScore && !aiResponse?.data?.candidateScore && !aiResponse?.data?.joinedScore && (
                        <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#555', textAlign: 'center', py: 2 }}>
                            No improvement suggestions available
                        </Typography>
                    )} */}
                </Paper>
            </Box>
        </Box>
    );
});

const ScoreGauge = React.memo(({ score }) => {
    const [value, setValue] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setValue(score || 0);
        }, 500);

        return () => clearTimeout(timer);
    }, [score]);

    const getLabel = (v) => {
        if (v <= 20) return "Poor";
        if (v <= 40) return "Fair";
        if (v <= 60) return "Average";
        if (v <= 80) return "Good";
        return "Excellent";
    };

    return (
        <Box sx={{ width: "250px", margin: "auto" }}>
            <GaugeComponent
                value={value}
                maxValue={100}
                arc={{
                    width: 0.15,
                    padding: 0.005,
                    cornerRadius: 3,
                    subArcs: [
                        { limit: 20, color: "#DF3C3C" },
                        { limit: 40, color: "#E9A663" },
                        { limit: 60, color: "#EEDB60" },
                        { limit: 80, color: "#9CC676" },
                        { limit: 100, color: "#4FA548" },
                    ],
                }}
                pointer={{
                    color: "#4B4B4B",
                    length: 0.75,
                    width: 15,
                    elastic: true
                }}
                labels={{
                    valueLabel: {
                        style: { fontSize: "18px", fontWeight: "bold", color: '#e40000ff' },
                        formatTextValue: () => `${getLabel(value)} - ${value}%`,
                    },
                }}
                type="semicircle"
            />
        </Box>
    );
});

const getScoreColor = (score) => {
    const numScore = parseInt(score);
    if (numScore >= 8) return '#4CAF50';
    if (numScore >= 6) return '#FF9800';
    if (numScore >= 4) return '#F44336';
    return '#9E9E9E';
};

const BAPerformance = () => {
    const theme = useTheme();
    const location = useLocation();
    const { recruiterUserId } = location?.state || {};
    const { Snackbar } = useContext(SnackbarContext);

    const [isSticky, setIsSticky] = useState(false);
    const [performanceVisible, setPerformanceVisible] = useState(false);
    const [baData, setBaData] = useState(null);
    const [transformedData, setTransformedData] = useState([]);
    const [AIresponse, setAIResponse] = useState(null);
    const [clientSummary, setClientSummary] = useState([])
    const [loading, setIsloading] = useState(false);
    const [score, setScore] = useState(0);

    const performanceRef = useRef(null);
    const mainContentRef = useRef(null);
    const [chartData, setChartData] = useState([]);
    const [selectedYear, setSelectedYear] = useState(2025)

    const [BAPerformance] = useViewBaPerformanceMutation();
    const [BaReferralList] = useBaReferralListMutation();
    const [cibilScore] = useBAPerformanceScoreMutation();
    const [overViewByAi] = useBAOverViewSummaryMutation();
    const [clientSummaryAPI] = useBAClientSummaryMutation()

    // ========== MEMOIZED VALUES ==========
    const hardcodedMetrics = useMemo(() => ({
        name: "Asha Kumar",
        city: "San Francisco, CA",
        designation: "Business Associate",
        experience: "2 yr 3 mo",
        status: "Online now"
    }), []);

    const performanceData = useMemo(() => [
        { label: 'Client Count', value: '0', change: '0', positive: true },
        { label: 'Job Count', value: '0', change: '0', positive: false },
        { label: 'Candidate Count', value: '0', change: '0', positive: true },
        { label: 'Joined Count', value: '0', change: '0', positive: true }
    ], []);

    const colors = useMemo(() => ({
        activeClients: "#19d2aaff",
        activeProfiles: "#035907ff",
        activePositions: "#f94525ff",
        joined: "#a115d8ff",
    }), []);

    // ========== CALLBACK FUNCTIONS ==========
    const getAIOverview = useCallback(async () => {
        if (!recruiterUserId) return;

        setIsloading(true);
        try {
            const payload = { recruiterUserId };
            const res = await overViewByAi(payload);
            setAIResponse(res?.data);
        } catch (err) {
            console.error('Error fetching AI overview:', err);
            Snackbar('Network error', 'error');
        } finally {
            setIsloading(false);
        }
    }, [recruiterUserId, overViewByAi, Snackbar]);

    const getPerformanceDetails = useCallback(async () => {
        if (!recruiterUserId) return;

        try {
            const payload = { recruiterUserId, year: selectedYear };
            const response = await BAPerformance(payload).unwrap();
            setBaData(response?.response?.data[0]);
            const formatted = response?.response?.data[0]?.monthlyRecruiterStats?.map((item) => ({
                name: item.monthName.slice(0, 3),
                activeClients: item.activeClientCount,
                activeProfiles: item.activeProfileCount,
                activePositions: item.activePositionCount,
                joined: item.monthlyJoinedCount,
            }));
            setChartData(formatted || []);
        } catch (error) {
            console.error("Failed to fetch details", error);
            Snackbar("Network Error", 'error');
        }
    }, [recruiterUserId, BAPerformance, Snackbar, selectedYear]);

    const getReferralList = useCallback(async () => {
        try {
            const params = { recruiterUserId: recruiterUserId, page: 0, size: 10 };
            const response = await BaReferralList(params).unwrap();
            const transformed = transformBackendData(response?.content || []);
            setTransformedData(transformed);
        } catch (error) {
            console.error("Failed to fetch details:", error);
            Snackbar("Network Error", 'error');
        }
    }, [BaReferralList, Snackbar]);

    const getCIBILscore = useCallback(async () => {
        if (!recruiterUserId) return;

        try {
            const params = { recruiterUserId };
            const res = await cibilScore(params);
            setScore(res?.data?.data?.BAPerformanceScore || 0);
        } catch (err) {
            console.error('Error fetching CIBIL score:', err);
            Snackbar('Network error', 'error');
        }
    }, [recruiterUserId, cibilScore, Snackbar]);

    const getClientSummary = useCallback(async () => {
        if (!recruiterUserId) return;

        setIsloading(true);
        try {
            const params = { recruiterUserId };
            const res = await clientSummaryAPI(params);
            setClientSummary(res?.data);
            console.log("res", res);

        } catch (err) {
            console.error('Error fetching AI client overview:', err);
            Snackbar('Network error', 'error');
        } finally {
            setIsloading(false);
        }
    }, [recruiterUserId, overViewByAi, Snackbar]);


    const transformBackendData = useCallback((activityFeeddata) => {
        return activityFeeddata.map(item => {
            let icon, color, type, timeline;

            switch (item.referralItemType) {
                case 'Candidate':
                    icon = UserCheck;
                    color = '#2196F3';
                    type = 'Candidate';
                    timeline = 'Candidate';
                    break;
                case 'Job Position':
                    icon = Briefcase;
                    color = '#4CAF50';
                    type = 'Job Opening';
                    timeline = 'Job';
                    break;
                case 'Client':
                    icon = Users;
                    color = '#9C27B0';
                    type = 'Client';
                    timeline = 'Client';
                    break;
                default:
                    icon = UserCheck;
                    color = '#673AB7';
                    type = 'Activity';
                    timeline = 'Other';
            }

            const formatTime = (dateString) => {
                if (!dateString) return 'Recently';
                const date = new Date(dateString);
                const now = new Date();
                const diffMs = now - date;
                const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                const diffDays = Math.floor(diffHours / 24);

                if (diffHours < 1) return 'Just now';
                if (diffHours < 24) return `${diffHours} hours ago`;
                if (diffDays < 7) return `${diffDays} days ago`;
                return `${Math.floor(diffDays / 7)} weeks ago`;
            };

            return {
                id: item.referralItemId,
                referralItemId: item.referralItemId,
                title: item.referralItemName,
                type: type,
                role: item.referralItemType === 'Job Position' ? 'Position' : item.referralItemType,
                company: item.referralItemEmail !== '-' ? item.referralItemEmail :
                    item.referralItemType === 'Client' ? item.referralItemName : '',
                time: formatTime(item.referralItemDate),
                category: item.referralItemType.toLowerCase().replace(' ', '-'),
                icon: icon,
                color: color,
                timeline: timeline,
                ...item
            };
        });
    }, []);

    // ========== DERIVED VALUES ==========
    const getExperienceText = useMemo(() => {
        if (baData?.joinedDate) {
            const joined = new Date(baData.joinedDate);
            const now = new Date();
            let years = now.getFullYear() - joined.getFullYear();
            let months = now.getMonth() - joined.getMonth();
            if (months < 0) {
                years--;
                months += 12;
            }
            if (years < 0) years = 0;
            if (months < 0) months = 0;
            return `${years} yr ${months} mo`;
        }
        return hardcodedMetrics.experience;
    }, [baData?.joinedDate, hardcodedMetrics.experience]);

    const getInitials = useMemo(() => {
        if (baData?.name) {
            return baData.name.split(' ').map(n => n[0]).join('').toUpperCase();
        }
        return hardcodedMetrics.name.split(' ').map(n => n[0]).join('').toUpperCase();
    }, [baData?.name, hardcodedMetrics.name]);

    const getStatus = useMemo(() => {
        if (baData?.status === "Approved") {
            return "Online now";
        }
        return hardcodedMetrics.status;
    }, [baData?.status, hardcodedMetrics.status]);

    // ========== EFFECTS ==========
    useEffect(() => {
        if (recruiterUserId) {
            getPerformanceDetails();
            getReferralList();
            getCIBILscore();
            getAIOverview();
            getClientSummary()
        }
    }, [recruiterUserId, getPerformanceDetails, getReferralList, getCIBILscore]);

    useEffect(() => {
        const handleScroll = () => {
            if (mainContentRef.current) {
                const scrollTop = mainContentRef.current.scrollTop;
                setIsSticky(scrollTop > 50);
            }
        };

        const performanceObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setPerformanceVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        const mainContent = mainContentRef.current;
        if (mainContent) {
            mainContent.addEventListener('scroll', handleScroll, { passive: true });
        }

        if (performanceRef.current) {
            performanceObserver.observe(performanceRef.current);
        }

        return () => {
            if (mainContent) {
                mainContent.removeEventListener('scroll', handleScroll);
            }
            performanceObserver.disconnect();
        };
    }, []);

    const ActivityFeed = useMemo(() => {
        return function ActivityFeedComponent() {
            const [filter, setFilter] = useState('all');

            const filteredActivities = useMemo(() => {
                if (filter === 'all') return transformedData;
                return transformedData.filter(activity => activity.referralItemType === filter);
            }, [filter, transformedData]);

            return (
                <Box sx={{ minHeight: 300, margin: '0 auto', marginTop: '20px', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '10px' }}>
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            gap: 2
                        }}>
                            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '14px', textAlign: 'center' }}>
                                Activity Feed
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                {['all', 'Candidate', 'Job Position', 'Client'].map((filterType) => (
                                    <Button key={filterType} variant={filter === filterType ? 'contained' : 'outlined'} size="small"
                                        onClick={() => setFilter(filterType)} sx={{
                                            textTransform: 'none',
                                            fontSize: '14px',
                                            backgroundColor: filter === filterType ? '#774EBF' : 'white',
                                            color: filter === filterType ? 'white' : 'black',
                                            borderColor: filter === filterType ? '#774EBF' : '#e0e0e0',
                                            '&:hover': {
                                                backgroundColor: filter === filterType ? '#6739a8' : '#f5f5f5',
                                                borderColor: filter === filterType ? '#6739a8' : '#d0d0d0'
                                            }
                                        }}>
                                        {filterType === 'all' ? 'All' :
                                            filterType === 'Job Position' ? 'Jobs' :
                                                filterType === 'Client' ? 'Clients' : 'Candidates'}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{
                            flexGrow: 1,
                            overflow: 'auto',
                            maxHeight: '500px',
                            padding: '7px',
                            '&::-webkit-scrollbar': { width: '6px' },
                            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '3px' },
                            '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '3px' },
                            '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' }
                        }}>
                            {filteredActivities.map((activity, index) => (
                                <div key={`${activity.referralItemId}-${index}`}>
                                    <ActivityCard activity={activity} />
                                </div>
                            ))}
                        </Box>
                        {filteredActivities.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Search size={32} color="#ccc" />
                                <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
                                    No activities found
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Try adjusting your search terms or filter
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            );
        };
    }, [transformedData]);

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <StickyHeader issticky={isSticky.toString()}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: { xs: '100%', md: 'auto' } }}>
                        <Avatar sx={{ width: 56, height: 56, bgcolor: theme.palette.primary.main, fontSize: '18px', fontWeight: 'bold' }}>
                            {getInitials}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '18px', mb: 0.5 }}>
                                {baData?.name || hardcodedMetrics.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                                        Joined {getExperienceText}
                                    </Typography>
                                    <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: 'text.secondary' }} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                                        {baData?.currentDesignation || hardcodedMetrics.designation}
                                    </Typography>
                                    <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: 'text.secondary' }} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                                        {baData?.city || hardcodedMetrics.city}
                                    </Typography>
                                    <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: 'text.secondary' }} />
                                </Box>
                                <StatusIndicator sx={{ ml: -0.5 }}>
                                    <Circle sx={{ fontSize: '8px' }} />
                                    {getStatus}
                                </StatusIndicator>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ flexShrink: 0, minWidth: { xs: '100%', md: '260px' }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ScoreGauge score={score} />
                    </Box>
                </Box>
            </StickyHeader>
            <MainContent ref={mainContentRef} sx={{ pb: 3, fontSize: '14px', flex: 1 }}>
                <Box>
                    <Box ref={performanceRef} sx={{ mt: 3, px: 2 }}>
                        <Fade in={performanceVisible} timeout={800}>
                            <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2, fontSize: '14px' }}>
                                Performance Overview
                            </Typography>
                        </Fade>
                        <Slide in={performanceVisible} direction="up" timeout={600}>
                            <Box container spacing={2} sx={{ mb: 3 }}>
                                {performanceData.map((item, index) => (
                                    <Box item xs={12} sm={6} md={3} key={index}>
                                        <Zoom in={performanceVisible} timeout={800 + (index * 200)}>
                                            <MetricCard>
                                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                    <Typography color="text.secondary" gutterBottom sx={{ fontSize: '12px', fontWeight: 500 }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '18px' }}>
                                                            {index === 0 ? baData?.refClientActiveCount :
                                                                index === 1 ? baData?.refPositionActiveCount :
                                                                    index === 2 ? baData?.refCandidateActiveCount :
                                                                        index === 3 ? baData?.JoinedCount : '0'}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '11px' }}>
                                                        {(index === 0 || index === 1) && (
                                                            `Inactive count: ${index === 0 ? baData?.refClientInactiveCount : index === 1 ? baData?.refPositionInactiveCount : baData?.refCandidateInactiveCount || '0'}`
                                                        )}
                                                        {(index === 3) && (
                                                            `Offerred count: ${index === 3 ? baData?.offeredCount : '0'}`
                                                        )}
                                                    </Typography>
                                                </CardContent>
                                            </MetricCard>
                                        </Zoom>
                                    </Box>
                                ))}
                            </Box>
                        </Slide>
                        <Fade in={performanceVisible} timeout={1000}>
                            <Box container spacing={2}>
                                <Box item xs={12} md={7}>
                                    <PerformanceContainer>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: "14px" }}>
                                                Performance Timeline
                                            </Typography>

                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="caption" sx={{ fontSize: "11px", color: "text.secondary" }}>
                                                    Filter by Year
                                                </Typography>
                                                <Select
                                                    size="small"
                                                    defaultValue="2025"
                                                    onChange={(e) => {
                                                        const year = parseInt(e.target.value);
                                                        setSelectedYear(year);
                                                    }}
                                                    sx={{
                                                        height: '30px',
                                                        fontSize: '12px',
                                                        '& .MuiSelect-select': {
                                                            padding: '6px 24px 6px 12px',
                                                            fontSize: '12px'
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="2023" sx={{ fontSize: '12px' }}>2023</MenuItem>
                                                    <MenuItem value="2024" sx={{ fontSize: '12px' }}>2024</MenuItem>
                                                    <MenuItem value="2025" sx={{ fontSize: '12px' }}>2025</MenuItem>
                                                    <MenuItem value="2026" sx={{ fontSize: '12px' }}>2026</MenuItem>
                                                </Select>
                                            </Box>

                                            {/* Legend dots */}
                                            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colors.activeProfiles }} />
                                                    <Typography variant="body2" sx={{ fontSize: "11px" }}>Candidates</Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colors.activePositions }} />
                                                    <Typography variant="body2" sx={{ fontSize: "11px" }}>Jobs</Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colors.activeClients }} />
                                                    <Typography variant="body2" sx={{ fontSize: "11px" }}>Clients</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={chartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Legend />
                                                <Line type="monotone" dataKey="activeClients" stroke={colors.activeClients} strokeWidth={2} />
                                                <Line type="monotone" dataKey="activeProfiles" stroke={colors.activeProfiles} strokeWidth={2} />
                                                <Line type="monotone" dataKey="activePositions" stroke={colors.activePositions} strokeWidth={2} />
                                                <Line type="monotone" dataKey="joined" stroke={colors.joined} strokeWidth={2} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </PerformanceContainer>
                                    <ActivityFeed />
                                </Box>
                                <Box item xs={12} md={5}>
                                    <ScrollableSection>
                                        <AIOverview aiResponse={AIresponse} clientSummary={clientSummary} loading={loading} />
                                    </ScrollableSection>
                                </Box>
                            </Box>
                        </Fade>
                    </Box>
                </Box>
            </MainContent>
        </Box>
    );
};

export default React.memo(BAPerformance);